import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import { MondayApiQueryInstance } from "../../../../services/monday/monday-service-ts";
import logger from "../../../../lib/logger";
import {
  buildDateValue,
  buildHourValue,
  buildTimelineValue,
  parseDateValue,
} from "../buildColumnValue";
import { EditorFrame, EditorProps } from "./EditorFrame";
import { Calendar, TimeSelect } from "./Calendar";

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Date with an optional time, edited in the viewer's monday profile time zone, the one monday
 * (and this view) shows the value in. The browser's zone is only the fallback.
 */
export const DateEditor = (props: EditorProps) => {
  const [timeZone, setTimeZone] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    MondayApiQueryInstance.getMyTimeZone()
      .then(
        (zone) => active && setTimeZone(zone && moment.tz.zone(zone) ? zone : moment.tz.guess()),
      )
      .catch((error) => {
        logger.error("Error loading the monday time zone", error);
        if (active) setTimeZone(moment.tz.guess());
      });
    return () => {
      active = false;
    };
  }, []);

  if (!timeZone) {
    return (
      <EditorFrame build={() => buildDateValue("", "")} dirty={false} {...props}>
        <p className="text-sm text-light-secondary-text-color">Loading…</p>
      </EditorFrame>
    );
  }
  return <ZonedDateEditor {...props} timeZone={timeZone} />;
};

const LINK_CLASS =
  "p-1 text-[13px] bg-transparent rounded border-0 cursor-pointer hover:bg-[#dcdfec] !font-figtree";

const ClockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#676879"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const RemoveButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    type="button"
    aria-label={label}
    className="flex justify-center items-center p-0 ml-auto w-7 h-7 bg-transparent rounded border-0 cursor-pointer hover:bg-[#dcdfec]"
    onClick={onClick}
  >
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#676879"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  </button>
);

/** "Bangkok time (GMT+07:00)": says which zone the time is in, since it may not be the computer's. */
const describeZone = (timeZone: string) =>
  `${timeZone.split("/").pop()?.replace(/_/g, " ")} time (GMT${moment.tz(timeZone).format("Z")})`;

const ZonedDateEditor = ({
  state,
  onCommit,
  onCancel,
  timeZone,
}: EditorProps & { timeZone: string }) => {
  const initial = parseDateValue(state.value, timeZone);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const today = moment.tz(timeZone).format("YYYY-MM-DD");

  // Without a time, picking a day is the whole edit, so it saves at once, as on monday.
  const pick = (day: string) => (time ? setDate(day) : onCommit(buildDateValue(day, "", timeZone)));

  return (
    <EditorFrame
      build={() => buildDateValue(date, time, timeZone)}
      dirty={date !== initial.date || time !== initial.time}
      onCommit={onCommit}
      onCancel={onCancel}
    >
      <Calendar
        openOn={date}
        today={today}
        markOf={(day) => (day === date ? "edge" : null)}
        onPick={pick}
      />
      <div className="flex flex-col gap-2 pt-2 w-full border-0 border-t border-solid border-[#e6e9ef]">
        {time ? (
          <div className="flex gap-1.5 items-center">
            <ClockIcon />
            <TimeSelect value={time} onChange={setTime} />
            <RemoveButton label="Remove time" onClick={() => setTime("")} />
          </div>
        ) : (
          <button
            type="button"
            className={`self-start text-[#0060b9] ${LINK_CLASS}`}
            onClick={() => {
              setTime("09:00");
              if (!date) setDate(today);
            }}
          >
            + Add time
          </button>
        )}
        <div className="flex justify-between items-center">
          <div className="flex gap-1">
            <button
              type="button"
              className={`px-2.5 border border-solid border-[#c3c6d4] ${LINK_CLASS}`}
              onClick={() => pick(today)}
            >
              Today
            </button>
            <button
              type="button"
              className={LINK_CLASS}
              onClick={() => onCommit(buildDateValue("", ""))}
            >
              Clear
            </button>
          </div>
          <span className="text-xs text-light-secondary-text-color">{describeZone(timeZone)}</span>
        </div>
      </div>
    </EditorFrame>
  );
};

export const HourEditor = ({ state, onCommit, onCancel }: EditorProps) => {
  const initial =
    state.value?.hour != null ? `${pad(state.value.hour)}:${pad(state.value.minute ?? 0)}` : "";
  const [time, setTime] = useState(initial);

  return (
    <EditorFrame
      build={() => buildHourValue(time)}
      dirty={time !== initial}
      onCommit={onCommit}
      onCancel={onCancel}
    >
      <div className="flex gap-1.5 items-center w-full">
        <ClockIcon />
        <TimeSelect value={time} onChange={setTime} />
        {time && <RemoveButton label="Clear hour" onClick={() => onCommit(buildHourValue(""))} />}
      </div>
    </EditorFrame>
  );
};

/** Timeline: the first click sets the start, the second the end (in either order). */
export const TimelineEditor = ({ state, onCommit, onCancel }: EditorProps) => {
  const initialFrom: string = state.value?.from ?? "";
  const initialTo: string = state.value?.to ?? "";
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const today = moment().format("YYYY-MM-DD");

  const pick = (day: string) => {
    if (!from || to) {
      setFrom(day);
      setTo("");
    } else if (day < from) {
      setFrom(day);
      setTo(from);
    } else {
      setTo(day);
    }
  };

  const describe = (day: string) => (day ? moment(day, "YYYY-MM-DD").format("MMM D, YYYY") : "…");

  return (
    <EditorFrame
      build={() => buildTimelineValue(from, to)}
      dirty={from !== initialFrom || to !== initialTo}
      onCommit={onCommit}
      onCancel={onCancel}
    >
      <p className="text-sm font-semibold">
        {from ? `${describe(from)} – ${describe(to)}` : "Choose a start date"}
      </p>
      <Calendar
        openOn={from}
        today={today}
        markOf={(day) =>
          day === from || day === to
            ? "edge"
            : from && to && day > from && day < to
              ? "range"
              : null
        }
        onPick={pick}
      />
      <div className="flex justify-between items-center pt-2 w-full border-0 border-t border-solid border-[#e6e9ef]">
        <button
          type="button"
          className={LINK_CLASS}
          onClick={() => onCommit(buildTimelineValue("", ""))}
        >
          Clear
        </button>
        <span className="text-xs text-light-secondary-text-color">Click away to save</span>
      </div>
    </EditorFrame>
  );
};
